import { useState, useRef, useEffect } from "react";
import { API_BASE } from "../api";

const WA_FALLBACK = "https://wa.me/40721512077?text=Bun%C4%83%20ziua%2C%20am%20o%20%C3%AEntrebare%20despre%20FiscalRisk.";
const MAX_ANON = 4;
const COUNT_KEY = "conformity_anon_count";

const WELCOME = "Bună! 👋 Sunt Fiscal Mind, asistentul virtual AI Risc Fiscal.\n\nTe pot ajuta cu:\n• Interpretarea scorului de risc fiscal\n• Întrebări despre SAF-T, TVA, ANAF\n• Plafoane de deductibilitate orientative\n• Cum funcționează platforma\n\nCe întrebare ai?";

const LIMIT_MSG = "Ai folosit cele 4 întrebări gratuite disponibile fără cont.\n\n🔐 Creează-ți un cont gratuit pe risc-fiscal.ro pentru a continua conversația, sau contactează-ne direct pe WhatsApp.";

export default function WhatsAppWidget() {
  const isLoggedIn = () => !!localStorage.getItem("token");

  const getAnonCount = () => parseInt(sessionStorage.getItem(COUNT_KEY) || "0", 10);
  const incAnonCount = () => sessionStorage.setItem(COUNT_KEY, String(getAnonCount() + 1));

  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [limitHit, setLimitHit] = useState(() => !isLoggedIn() && getAnonCount() >= MAX_ANON);
  const bottomRef               = useRef(null);
  const inputRef                = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading || limitHit) return;
    setInput("");

    const userMsg = { role: "user", content: text };
    const history = messages.filter(m => m.role !== "assistant" || m.content !== WELCOME);
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Creste contorul pentru utilizatori anonimi
    if (!isLoggedIn()) incAnonCount();

    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Eroare");
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);

      // Verifica daca s-a atins limita dupa acest mesaj
      if (!isLoggedIn() && getAnonCount() >= MAX_ANON) {
        setTimeout(() => {
          setMessages(prev => [...prev, { role: "assistant", content: LIMIT_MSG }]);
          setLimitHit(true);
        }, 400);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Ne pare rău, a apărut o problemă. Poți încerca din nou sau ne contactezi pe WhatsApp.",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={S.wrap}>
      {open && (
        <div style={S.panel} role="dialog" aria-label="Fiscal Mind - Asistent virtual AI Risc Fiscal">
          {/* Header */}
          <div style={S.header}>
            <div style={S.avatar}>
              <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
              </svg>
            </div>
            <div style={S.headerText}>
              <div style={S.botName}>Fiscal Mind</div>
              <div style={S.botStatus}><span style={S.dot} />Asistent virtual AI Risc Fiscal</div>
            </div>
            <button style={S.closeBtn} onClick={() => setOpen(false)} aria-label="Închide chat">×</button>
          </div>

          {/* Disclaimer */}
          <div style={S.disclaimer}>
            ℹ️ Informații orientative — nu înlocuiesc consultanța fiscală autorizată.
          </div>

          {/* Mesaje */}
          <div style={S.body}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                <div style={m.role === "user" ? S.bubbleUser : S.bubbleBot}>
                  {m.content.split("\n").map((line, j) => (
                    <span key={j}>{line}{j < m.content.split("\n").length - 1 && <br />}</span>
                  ))}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
                <div style={{ ...S.bubbleBot, color: "rgba(233,236,242,0.45)", fontStyle: "italic" }}>
                  Fiscal Mind scrie...
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Butoane dupa limita */}
          {limitHit && (
            <div style={S.limitActions}>
              <a href="/signup" style={S.btnPrimary}>Creează cont gratuit</a>
              <a href={WA_FALLBACK} target="_blank" rel="noopener noreferrer" style={S.btnSecondary}>
                WhatsApp
              </a>
            </div>
          )}

          {/* Input — ascuns dupa limita */}
          {!limitHit && (
            <div style={S.inputWrap}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrie o întrebare..."
                rows={1}
                style={S.textarea}
                disabled={loading}
                aria-label="Mesaj pentru Fiscal Mind"
              />
              <button onClick={send} disabled={loading || !input.trim()} style={S.sendBtn} aria-label="Trimite mesaj">
                <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          )}

          {/* WhatsApp fallback */}
          {!limitHit && (
            <div style={S.waFallback}>
              <a href={WA_FALLBACK} target="_blank" rel="noopener noreferrer" style={S.waLink}>
                Consultanță personalizată → WhatsApp
              </a>
            </div>
          )}
        </div>
      )}

      {/* Buton flotant */}
      <button style={S.fab} onClick={() => setOpen(o => !o)} aria-label="Deschide Fiscal Mind - Asistent virtual">
        {open ? (
          <span style={{ fontSize: 22, lineHeight: 1 }}>×</span>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
            </svg>
            <span style={S.fabLabel}>Fiscal Mind</span>
          </>
        )}
      </button>
    </div>
  );
}

const S = {
  wrap: {
    position: "fixed", bottom: 24, right: 24, zIndex: 1100,
    display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12,
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
  },
  panel: {
    width: 340, borderRadius: 18, overflow: "hidden",
    boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "#0f1624",
    display: "flex", flexDirection: "column",
    maxHeight: "70vh",
  },
  header: {
    background: "#1a6b3a", padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
  },
  avatar: {
    width: 38, height: 38, borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  headerText: { flex: 1 },
  botName: { color: "#fff", fontWeight: 800, fontSize: 15 },
  botStatus: { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "rgba(255,255,255,0.8)" },
  dot: { width: 6, height: 6, borderRadius: "50%", background: "#4ade80", display: "inline-block" },
  closeBtn: { background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", padding: 0 },
  disclaimer: {
    padding: "6px 14px", background: "rgba(232,196,96,0.08)",
    borderBottom: "1px solid rgba(232,196,96,0.15)",
    fontSize: 11, color: "rgba(232,196,96,0.8)", flexShrink: 0,
  },
  body: {
    flex: 1, overflowY: "auto", padding: "14px 12px",
    display: "flex", flexDirection: "column",
  },
  bubbleBot: {
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "4px 14px 14px 14px", padding: "10px 13px",
    fontSize: 13, lineHeight: 1.6, color: "rgba(233,236,242,0.9)",
    maxWidth: "90%",
  },
  bubbleUser: {
    background: "#1a3a6b", borderRadius: "14px 4px 14px 14px",
    padding: "10px 13px", fontSize: 13, lineHeight: 1.6,
    color: "rgba(233,236,242,0.95)", maxWidth: "85%",
  },
  limitActions: {
    display: "flex", gap: 10, padding: "12px 14px",
    borderTop: "1px solid rgba(255,255,255,0.1)", flexShrink: 0,
  },
  btnPrimary: {
    flex: 1, background: "#1a6b3a", color: "#fff", borderRadius: 10,
    padding: "9px 14px", fontSize: 13, fontWeight: 700,
    textDecoration: "none", textAlign: "center",
  },
  btnSecondary: {
    background: "rgba(255,255,255,0.08)", color: "rgba(233,236,242,0.8)",
    borderRadius: 10, padding: "9px 14px", fontSize: 13,
    textDecoration: "none", textAlign: "center",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  inputWrap: {
    display: "flex", gap: 8, padding: "10px 12px",
    borderTop: "1px solid rgba(255,255,255,0.08)", flexShrink: 0,
    alignItems: "flex-end",
  },
  textarea: {
    flex: 1, background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
    padding: "8px 12px", color: "#e6e8ee", fontSize: 13,
    resize: "none", outline: "none", lineHeight: 1.5,
    fontFamily: "inherit",
  },
  sendBtn: {
    width: 36, height: 36, borderRadius: "50%", border: "none",
    background: "#1a6b3a", cursor: "pointer", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  waFallback: {
    padding: "8px 14px 12px", flexShrink: 0,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    textAlign: "center",
  },
  waLink: {
    fontSize: 11, color: "rgba(203,213,225,0.45)",
    textDecoration: "none",
  },
  fab: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 20px", borderRadius: 50,
    background: "#1a6b3a", color: "#fff", border: "none",
    fontWeight: 800, fontSize: 14, cursor: "pointer",
    boxShadow: "0 4px 20px rgba(26,107,58,0.45)",
  },
  fabLabel: { fontSize: 14, fontWeight: 800 },
};
